// ABOUTME: Daily behaviors tracking card showing today's logged behaviors for a specific child with developmental averages
// ABOUTME: Features progress bars that can extend beyond developmental recommendations and real-time behavior count updates

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { StarOutlineRounded } from '@mui/icons-material';
import { TriangleIcon, StormIcon } from '../utils/behaviorUtils';
import { Child, BehaviorType, BehaviorRecord, DevelopmentalAverageDto } from '../types/api';
import { behaviorAPI, adminAPI } from '../services/api';
import { getTodayForBehaviors, getThisWeekDateRange } from '../utils/dateUtils';
import PositivityDonutChart from './PositivityDonutChart';
import naluV1Theme from '../theme/theme.nalu-v1';

interface DailyBehaviorsCardProps {
  child: Child;
  familyId: number;
  timezone: string;
  timeWindow: string;
}

interface BehaviorCount {
  stars: number;
  triangles: number;
  squares: number;
}

interface WeeklyBehaviorCount {
  squares: number;
}

interface BehaviorRecommendations {
  stars: number | null;
  triangles: number | null;
  squares: number | null;
}

const DailyBehaviorsCard: React.FC<DailyBehaviorsCardProps> = ({ child, familyId, timezone, timeWindow }) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [behaviorCounts, setBehaviorCounts] = useState<BehaviorCount>({
    stars: 0,
    triangles: 0,
    squares: 0
  });
  const [weeklyBehaviorCounts, setWeeklyBehaviorCounts] = useState<WeeklyBehaviorCount>({
    squares: 0
  });
  const [recommendations, setRecommendations] = useState<BehaviorRecommendations>({
    stars: null,
    triangles: null,
    squares: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDailyBehaviors();
    loadWeeklyBehaviors();
    loadDevelopmentalAverages();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load functions intentionally omitted; they are stable and adding them would cause extra fetches
  }, [child.id, child.childId, familyId, timezone, timeWindow]);


  const loadDailyBehaviors = async () => {
    try {
      // Get the child ID (could be either 'id' or 'childId')
      const childId = child.id || child.childId;
      if (!childId) {
        console.error('No child ID available');
        setError('Unable to load behaviors - child ID missing');
        return;
      }

      // This is a Time-Independent Card - always shows TODAY regardless of time window selection
      // Use family timezone-aware "today" for behavior tracking (respects 3am daily reset)
      const todayForBehaviors = getTodayForBehaviors(timezone);
      
      // Get today's behaviors only
      const behaviors = await behaviorAPI.getChildBehaviorsByDate(childId, todayForBehaviors, timezone);

      // Count behaviors by type
      const counts = behaviors.reduce((acc, behavior) => {
        switch (behavior.behaviorType) {
          case BehaviorType.Star:
            acc.stars++;
            break;
          case BehaviorType.Triangle:
            acc.triangles++;
            break;
          case BehaviorType.Square:
            acc.squares++;
            break;
          default:
            break;
        }
        return acc;
      }, { stars: 0, triangles: 0, squares: 0 });

      setBehaviorCounts(counts);
    } catch (error) {
      console.error('Failed to load daily behaviors:', error);
      setError('Error loading behaviors. Please try again.');
    }
  };

  const loadWeeklyBehaviors = async () => {
    try {
      // Get the child ID (could be either 'id' or 'childId')
      const childId = child.id || child.childId;
      if (!childId) {
        console.error('No child ID available');
        setError('Unable to load weekly behaviors - child ID missing');
        return;
      }

      // Get this week's date range (Sunday to Saturday) in family timezone
      const weekRange = getThisWeekDateRange(timezone);

      // Get this week's behaviors using paginated API (Issue #1081)
      const response = await behaviorAPI.getBehaviorRecords(
        familyId,
        weekRange.startDate,
        weekRange.endDate,
        childId,
        undefined, // behaviorTypes
        undefined, // days
        1, // page
        1000 // pageSize - get all for this week (unlikely to exceed 1000)
      );

      // Count only squares for this week
      const weeklySquares = response.data.filter((behavior: BehaviorRecord) =>
        behavior.behaviorType === BehaviorType.Square
      ).length;

      setWeeklyBehaviorCounts({ squares: weeklySquares });
    } catch (error) {
      console.error('Failed to load weekly behaviors:', error);
      setError('Error loading weekly behaviors. Please try again.');
    }
  };

  const loadDevelopmentalAverages = async () => {
    try {
      const averages = await adminAPI.getDevelopmentalAveragesAdmin();
      
      // Calculate child's age
      const childAge = calculateChildAge(child);
      
      // Find matching age group for each behavior type
      const starAverage = findAverageForAge(averages, childAge, BehaviorType.Star);
      const triangleAverage = findAverageForAge(averages, childAge, BehaviorType.Triangle);
      const squareAverage = findAverageForAge(averages, childAge, BehaviorType.Square);

      setRecommendations({
        stars: starAverage,
        triangles: triangleAverage,
        squares: squareAverage
      });
    } catch (error) {
      console.error('Failed to load developmental averages:', error);
      setError('Error loading developmental averages. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateChildAge = (child: Child): number => {
    const birthDate = child.dateOfBirth ? 
      new Date(child.dateOfBirth) : 
      new Date(child.birthYear, (child.birthMonth || 1) - 1, child.birthDay || 1);
    
    const today = new Date();
    const ageInYears = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return ageInYears - 1;
    }
    return ageInYears;
  };

  const findAverageForAge = (
    averages: DevelopmentalAverageDto[], 
    childAge: number, 
    behaviorType: BehaviorType
  ): number | null => {
    const match = averages.find(avg => 
      avg.behaviorType === behaviorType && 
      childAge >= avg.ageGroupStart && 
      childAge <= avg.ageGroupEnd
    );
    return match ? match.averagePerDay : null;
  };

  const calculateProgressPercentage = (actual: number, recommended: number | null): number => {
    if (recommended === null || recommended === undefined || recommended === 0) return 0;
    return Math.round((actual / recommended) * 100);
  };

  const getProgressBarValue = (actual: number, recommended: number | null): number => {
    if (recommended === null || recommended === undefined || recommended === 0) {
      // Show minimal progress (10%) when behaviors exist but no recommendation
      return actual > 0 ? 10 : 0;
    }
    return Math.min(calculateProgressPercentage(actual, recommended), 100);
  };

  const formatCountDisplay = (actual: number, recommended: number | null, behaviorType: BehaviorType) => {
    const recommendationText = recommended === null || recommended === undefined ? '-' : Math.round(recommended).toString();

    switch (behaviorType) {
      case BehaviorType.Star:
        return (
          <>
            <strong>{actual}</strong> <strong>Today</strong> | <strong>{recommendationText}</strong> Daily Target
          </>
        );
      case BehaviorType.Triangle:
        return (
          <>
            <strong>{actual}</strong> <strong>Today</strong> | <strong>{recommendationText}</strong> Daily Age Appropriate Benchmark
          </>
        );
      case BehaviorType.Square: {
        const weeklyRecommendation = recommended === null || recommended === undefined ? '-' : Math.round(recommended * 7).toString();
        return (
          <>
            <strong>{actual}</strong> <strong>This Week</strong> | <strong>{weeklyRecommendation}</strong> Weekly Age Appropriate Benchmark
          </>
        );
      }
      default:
        return `${actual}/-`;
    }
  };

  const getCardTitle = (_timeWindow: string): string => {
    // For child analytics, always show the new format regardless of time window
    // since this card is Time-Independent and always shows TODAY's behaviors
    return 'Daily Logged Behaviors vs Daily Target/Age Appropriate Benchmark';
  };

  if (isLoading) {
    return (
      <Card data-testid="daily-behaviors-card">
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom textAlign="center" sx={{ fontWeight: 600, mb: 3 }}>
            {getCardTitle(timeWindow)}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading behaviors...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="daily-behaviors-card">
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom textAlign="center" sx={{ fontWeight: 600, mb: 3 }}>
            {getCardTitle(timeWindow)}
          </Typography>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="daily-behaviors-card">
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom textAlign="center" sx={{ fontWeight: 600, mb: 3 }}>
          {getCardTitle(timeWindow)}
        </Typography>

        {/* Positivity Donut Chart - Issue #962 */}
        <Box
          display="flex"
          justifyContent="center"
          sx={{ mb: 3 }}
          data-testid="positivity-chart-container"
        >
          <PositivityDonutChart
            behaviorCounts={{
              stars: behaviorCounts.stars,
              triangles: behaviorCounts.triangles,
              squares: behaviorCounts.squares
            }}
            size={200}
          />
        </Box>

        <Stack spacing={3}>
          {/* Stars Progress */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 32 }}>
                <StarOutlineRounded sx={{ color: 'success.main', fontSize: 20 }} />
              </Box>
              <Typography variant="body2" sx={{ ml: 1, flexGrow: 1 }}>
                {formatCountDisplay(behaviorCounts.stars, recommendations.stars, BehaviorType.Star)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getProgressBarValue(behaviorCounts.stars, recommendations.stars)}
              sx={{
                height: 8,
                borderRadius: isNaluV1Theme ? '48px' : 4,
                backgroundColor: (theme) => alpha(theme.palette.common.black, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: isNaluV1Theme ? '48px' : 4,
                  backgroundColor: theme => theme.palette.success.main // Green for stars matching icon
                }
              }}
              aria-valuenow={getProgressBarValue(behaviorCounts.stars, recommendations.stars)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Stars progress toward developmental average"
              data-testid="star-progress-bar"
            />
            {/* Extended bar visualization when exceeding recommendation */}
            {recommendations.stars && behaviorCounts.stars > recommendations.stars && (
              <Box sx={{ position: 'relative', mt: -1 }}>
                <LinearProgress
                  variant="determinate"
                  value={100}
                  sx={{
                    height: 8,
                    borderRadius: isNaluV1Theme ? '48px' : 4,
                    backgroundColor: 'transparent',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: isNaluV1Theme ? '48px' : 4,
                      backgroundColor: theme => theme.palette.success.light, // Lighter green for excess
                      opacity: 0.6
                    }
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Triangles Progress */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 32 }}>
                <TriangleIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="body2" sx={{ ml: 1, flexGrow: 1 }}>
                {formatCountDisplay(behaviorCounts.triangles, recommendations.triangles, BehaviorType.Triangle)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getProgressBarValue(behaviorCounts.triangles, recommendations.triangles)}
              sx={{
                height: 8,
                borderRadius: isNaluV1Theme ? '48px' : 4,
                backgroundColor: (theme) => alpha(theme.palette.common.black, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: isNaluV1Theme ? '48px' : 4,
                  backgroundColor: theme => theme.palette.warning.main // Yellow for triangles matching icon
                }
              }}
              aria-valuenow={getProgressBarValue(behaviorCounts.triangles, recommendations.triangles)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Triangles progress toward developmental average"
              data-testid="triangle-progress-bar"
            />
            {/* Extended bar visualization when exceeding recommendation */}
            {recommendations.triangles && behaviorCounts.triangles > recommendations.triangles && (
              <Box sx={{ position: 'relative', mt: -1 }}>
                <LinearProgress
                  variant="determinate"
                  value={100}
                  sx={{
                    height: 8,
                    borderRadius: isNaluV1Theme ? '48px' : 4,
                    backgroundColor: 'transparent',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: isNaluV1Theme ? '48px' : 4,
                      backgroundColor: theme => theme.palette.warning.light, // Lighter yellow for excess
                      opacity: 0.6
                    }
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Squares Progress */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 32 }}>
                <StormIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="body2" sx={{ ml: 1, flexGrow: 1 }}>
                {formatCountDisplay(weeklyBehaviorCounts.squares, recommendations.squares, BehaviorType.Square)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getProgressBarValue(weeklyBehaviorCounts.squares, recommendations.squares ? recommendations.squares * 7 : null)}
              sx={{
                height: 8,
                borderRadius: isNaluV1Theme ? '48px' : 4,
                backgroundColor: (theme) => alpha(theme.palette.common.black, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: isNaluV1Theme ? '48px' : 4,
                  backgroundColor: theme => theme.palette.error.main // Red for squares matching icon
                }
              }}
              aria-valuenow={getProgressBarValue(weeklyBehaviorCounts.squares, recommendations.squares ? recommendations.squares * 7 : null)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Squares progress toward developmental average"
              data-testid="square-progress-bar"
            />
            {/* Extended bar visualization when exceeding recommendation */}
            {recommendations.squares && weeklyBehaviorCounts.squares > (recommendations.squares * 7) && (
              <Box sx={{ position: 'relative', mt: -1 }}>
                <LinearProgress
                  variant="determinate"
                  value={100}
                  sx={{
                    height: 8,
                    borderRadius: isNaluV1Theme ? '48px' : 4,
                    backgroundColor: 'transparent',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: isNaluV1Theme ? '48px' : 4,
                      backgroundColor: theme => theme.palette.error.light, // Lighter red for excess
                      opacity: 0.6
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DailyBehaviorsCard;